import {
  age_filter_id,
  approval_rate_id,
  country_filter_id,
} from "../constants";
import { FilterId } from "../types";
import { ProlificStudyId, StudyStatus } from "./types";
import type { CountryIso3 } from "@/components/outreach/recruitment/panelPrediction/panelRegistry/countries";

export const FIXED_SCREEN_OUT_CODE_TYPE = "FIXED_SCREEN_OUT" as const;
export const FIXED_SCREEN_OUT_PAYMENT_ACTION_TYPE =
  "FIXED_SCREEN_OUT_PAYMENT" as const;

// Prolific rejects study names (mapped from external title) > 120 chars (error 140003).
export const PROLIFIC_MAX_STUDY_NAME_LENGTH = 120;
// Prolific caps a mutually exclusive study collection at 10 studies (error 140003).
export const PROLIFIC_MAX_STUDIES_PER_COLLECTION = 10;
export const PROLIFIC_LAUNCH_TARGET_MULTIPLIER = 3;

export const country_of_birth_filter_id = "country-of-birth" as FilterId;
// Free-form Prolific participant-id filter: only the listed participants can
// see and take the study. Exposed in the UI under Admin Options (super-admin).
export const custom_allowlist_filter_id = "custom_allowlist" as FilterId;
// Participant-id blocklist built from org participation history (cooldowns,
// relaunch carry-forward).
export const custom_blocklist_filter_id = "custom_blocklist" as FilterId;
export const completion_code_response_incomplete_error =
  "response-not-finished";

export const PROLIFIC_URL_PARAMS = ["__pid", "__study_id", "__submission_id"];
export const prolific_search_param_string =
  "__pid={{%PROLIFIC_PID%}}&__study_id={{%STUDY_ID%}}&__submission_id={{%SESSION_ID%}}";

export const additional_screen_out_text =
  "You have been screened out and will be redirected to Prolific. You will receive payment for the time you spent participating in this study.";

export const redirection_url = (code: string) => {
  return `https://app.prolific.com/submissions/complete?cc=${code}`;
};

export const message_link = (channel_id: string) => {
  return `https://app.prolific.com/messages/${channel_id}`;
};

export const study_url = (study_id: ProlificStudyId, status: StudyStatus) => {
  return (
    `https://app.prolific.com/researcher/workspaces/studies/${study_id}` +
    (status !== "UNPUBLISHED" ? "/submissions" : "")
  );
};

export const COUNTRY_ID_TO_NAME = {
  "0": "United Kingdom",
  "1": "United States",
  "2": "Ireland",
  "3": "Germany",
  "4": "France",
  "5": "Spain",
  "19": "Australia",
  "20": "Austria",
  "27": "Belgium",
  "37": "Brazil",
  "45": "Canada",
  "50": "Chile",
  "61": "Croatia",
  "65": "Czech Republic",
  "66": "Denmark",
  "75": "Estonia",
  "80": "Finland",
  "89": "Greece",
  "104": "Hungary",
  "105": "Iceland",
  "111": "Israel",
  "112": "Italy",
  "114": "Japan",
  "120": "Korea",
  "124": "Latvia",
  "131": "Luxembourg",
  "145": "Mexico",
  "158": "Netherlands",
  "160": "New Zealand",
  "167": "Norway",
  "178": "Poland",
  "179": "Portugal",
  "203": "Slovakia",
  "204": "Slovenia",
  "207": "South Africa",
  "215": "Sweden",
  "216": "Switzerland",
} as const satisfies Record<string, string>;

export const COUNTRY_ID_TO_ISO3 = {
  "0": "GBR",
  "1": "USA",
  "2": "IRL",
  "3": "DEU",
  "4": "FRA",
  "5": "ESP",
  "19": "AUS",
  "20": "AUT",
  "27": "BEL",
  "37": "BRA",
  "45": "CAN",
  "50": "CHL",
  "61": "HRV",
  "65": "CZE",
  "66": "DNK",
  "75": "EST",
  "80": "FIN",
  "89": "GRC",
  "104": "HUN",
  "105": "ISL",
  "111": "ISR",
  "112": "ITA",
  "114": "JPN",
  "120": "KOR",
  "124": "LVA",
  "131": "LUX",
  "145": "MEX",
  "158": "NLD",
  "160": "NZL",
  "167": "NOR",
  "178": "POL",
  "179": "PRT",
  "203": "SVK",
  "204": "SVN",
  "207": "ZAF",
  "215": "SWE",
  "216": "CHE",
} satisfies Record<keyof typeof COUNTRY_ID_TO_NAME, CountryIso3>;

export type DefaultFilter =
  | {
      type: "range";
      filter_id: FilterId;
      selected_range: { lower: number; upper: number };
      min: number;
      max: number;
      data_type: "integer";
    }
  | {
      type: "select";
      filter_id: FilterId;
      selected_values: string[];
      choices: Record<string, string>;
    };

export const DEFAULT_PROLIFIC_AGE_FILTER = {
  type: "range" as const,
  filter_id: age_filter_id,
  selected_range: { lower: 18, upper: 99 },
  min: 18,
  max: 99,
  data_type: "integer" as const,
};

export const DEFAULT_PROLIFIC_FILTERS: DefaultFilter[] = [
  DEFAULT_PROLIFIC_AGE_FILTER,
  {
    type: "range" as const,
    filter_id: approval_rate_id,
    selected_range: { lower: 100, upper: 100 },
    min: 0,
    max: 100,
    data_type: "integer",
  },
  {
    type: "select" as const,
    filter_id: country_filter_id,
    selected_values: ["1"], // US
    choices: COUNTRY_ID_TO_NAME,
  },
];

export const MAX_SCREENING_TIME_MS = 8 * 60 * 1000;
export const PROLIFIC_API_CALL_GET_REQUEST_TIMEOUT_MS = 15000;
export const PROLIFIC_ADD_STUDY_PLACES_MAX_RETRIES = 10;
export const PROLIFIC_ADD_STUDY_PLACES_INITIAL_RETRY_DELAY_MS = 1000;
export const PROLIFIC_ADD_STUDY_PLACES_MAX_RETRY_DELAY_MS = 10000;
// Prolific serializes study status changes per workspace and answers a
// concurrent transition with HTTP 429 error_code 150100. The lock clears
// within a few seconds, so the transition is retried with backoff.
export const PROLIFIC_STATUS_CHANGE_LOCK_MAX_RETRIES = 5;
export const PROLIFIC_STATUS_CHANGE_LOCK_INITIAL_RETRY_DELAY_MS = 1000;
export const PROLIFIC_STATUS_CHANGE_LOCK_MAX_RETRY_DELAY_MS = 8000;
export const PROLIFIC_WEBHOOK_ACTIVITY_START_TO_CLOSE_TIMEOUT_MS =
  5 * 60 * 1000;
// This needs to be at least 24hours as duplicate events can occur for quite a few hours.
export const PROLIFIC_EVENT_MAX_AGE_MS = 31 * 24 * 3600 * 1000; // 31 days for replay and auditing purposes
export const PROLIFIC_JOB_SCHEDULER_TIME_INTERVAL_S = 3600; // 1 hour

// TODO(future): update rates to be in dollars rather than cents
export const SCREENOUT_REWARD_CENTS_PER_HOUR = 8 * 100;
export const APPROVE_REWARD_MINI_CENTS_PER_HOUR = 12 * 100;
// Prolific minimum fixed screen-out reward ($0.14)
export const MIN_FIXED_SCREENOUT_REWARD_CENTS = 14;
export const MAX_FIXED_SCREENOUT_REWARD_CENTS = 70;
// Landing, consent and loading before the first answer (prod median ~22s)
export const SCREENER_OVERHEAD_MINUTES = 0.5;
export const MIN_REWARD_TOTAL_CENTS = 80; // 80 cents
// Our Prolific workspace is billed in USD, so rewards are USD cents.
export const PROLIFIC_REWARD_CURRENCY = "USD";
// Measured from the ledger: Prolific's fee is 30% of the total charge.
export const PROLIFIC_FEE_SHARE_OF_TOTAL = 0.3;
export const APPROVE_REWARD_SHORT_CENTS_PER_HOUR = 12 * 100;
export const APPROVE_REWARD_REGULAR_CENTS_PER_HOUR = 12 * 100;
export const VIDEO_STUDY_ADDITIONAL_REWARD_CENTS_PER_HOUR = 4 * 100;
export const MAX_REWARD_CENTS_PER_HOUR = 16 * 100;

export const INVALID_DATA_STATES = ["CONSENT_REVOKED", "DATA_EXPIRED"];

// https://researcher-help.prolific.com/en/article/2bb2f4
export const PROLIFIC_COUNTRIES = [
  "AUSTRALIA",
  "AUSTRIA",
  "BELGIUM",
  "BRAZIL",
  "CANADA",
  "CHILE",
  "CROATIA",
  "CZECH REPUBLIC",
  "DENMARK",
  "ESTONIA",
  "FINLAND",
  "FRANCE",
  "GERMANY",
  "GREECE",
  "HUNGARY",
  "ICELAND",
  "IRELAND",
  "ISRAEL",
  "ITALY",
  "JAPAN",
  "KOREA",
  "LATVIA",
  "LUXEMBOURG",
  "MEXICO",
  "NETHERLANDS",
  "NEW ZEALAND",
  "NORWAY",
  "POLAND",
  "PORTUGAL",
  "SLOVAKIA",
  "SLOVENIA",
  "SOUTH AFRICA",
  "SPAIN",
  "SWEDEN",
  "SWITZERLAND",
  "UNITED KINGDOM",
  "UNITED STATES",
];

export enum RequestReturnReason {
  NO_PLATFORM_VISIT = "Completed study without visiting our platform",
  DIDNT_FINISH_STUDY = "Didn't finish the study",
  SCREENER_BYPASS = "Attempted to bypass screener questions",
  UNEXPECTED_ERROR = "There was an error with your submission. Reply to this message if you think this is not the case.",
}

export enum ScreenOutReason {
  SCREENED_OUT = "Screened out",
}

export const LOW_BALANCE_THRESHOLD_CENTS = 10000 * 100;

export const professional_role_filter_id = "job-title" as FilterId;
export const PROFESSIONAL_ROLE_VERIFICATION = "corroborated";
// Prolific enforces this minimum for studies targeting verified professionals.
export const VERIFIED_PROFESSIONAL_MIN_REWARD_CENTS_PER_HOUR = 60 * 100;

// Only recruits filled this recently get topped up (p90 of hide-after-fill is 5.9 days).
export { RECRUIT_TOP_UP_WINDOW_MS as PROLIFIC_TOP_UP_WINDOW_MS } from "../constants";
// Hides younger than this still count as visible (p50 of hide-to-unhide is ~2h).
export { RECRUIT_TOP_UP_UNHIDE_GRACE_MS as PROLIFIC_TOP_UP_UNHIDE_GRACE_MS } from "../constants";
// Longer than the top-up activity's 15-minute timeout, so an add still in flight is never settled.
export { RECRUIT_TOP_UP_SETTLE_AFTER_MS as PROLIFIC_TOP_UP_SETTLE_AFTER_MS } from "../constants";
// The per-recruit dummy row (created_at = epoch) holds our own count adjustments.
export const DUMMY_ROW_CREATED_BEFORE = "2000-01-01T00:00:00.000Z";
